import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Toast from '../../components/ui/Toast';
import { serviceService } from '../../services/serviceService';
import { propertyService } from '../../services/propertyService';
import { vehicleService } from '../../services/vehicleService';
import { useAuth } from '../../contexts/AuthContext';
import ServiceForm from './components/ServiceForm';
import PropertyForm from './components/PropertyForm';
import VehicleForm from './components/VehicleForm';
import DeleteConfirmModal from './components/DeleteConfirmModal';

const AdminCRUDManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('services');
  const [toast, setToast] = useState(null);

  // Pagination state
  const [servicesPage, setServicesPage] = useState(1);
  const [propertiesPage, setPropertiesPage] = useState(1);
  const [vehiclesPage, setVehiclesPage] = useState(1);
  const itemsPerPage = 10;

  // Services state
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [serviceSearchQuery, setServiceSearchQuery] = useState('');
  const [serviceCategory, setServiceCategory] = useState('all');
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingService, setEditingService] = useState(null);

  // Properties state
  const [properties, setProperties] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(true);
  const [propertySearchQuery, setPropertySearchQuery] = useState('');
  const [propertyCategory, setPropertyCategory] = useState('all');
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);

  // Vehicles state
  const [vehicles, setVehicles] = useState([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(true);
  const [vehicleSearchQuery, setVehicleSearchQuery] = useState('');
  const [vehicleType, setVehicleType] = useState('all');
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);

  // Delete confirmation
  const [deleteModal, setDeleteModal] = useState({ show: false, type: '', id: null, name: '' });

  // Check admin access
  useEffect(() => {
    if (userProfile && userProfile?.role !== 'admin') {
      navigate('/admin-login');
    }
  }, [userProfile, navigate]);

  // Handle URL query parameters for tab selection
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params?.get('tab');
    if (tab && ['services', 'properties', 'vehicles']?.includes(tab)) {
      setActiveTab(tab);
    }
  }, [location?.search]);

  // Load data based on active tab
  useEffect(() => {
    if (activeTab === 'services') {
      loadServices();
    } else if (activeTab === 'properties') {
      loadProperties();
    } else if (activeTab === 'vehicles') {
      loadVehicles();
    }
  }, [activeTab]);

  // Services functions
  const loadServices = async () => {
    try {
      setServicesLoading(true);
      const data = await serviceService?.getAll();
      setServices(data);
    } catch (err) {
      setToast({ message: 'Erreur lors du chargement des services', type: 'error' });
    } finally {
      setServicesLoading(false);
    }
  };

  const handleServiceSubmit = async (formData) => {
    try {
      if (editingService) {
        await serviceService?.update(editingService?.id, formData);
        setToast({ message: 'Service modifié avec succès!', type: 'success' });
      } else {
        await serviceService?.create(formData);
        setToast({ message: 'Service créé avec succès!', type: 'success' });
      }
      setShowServiceModal(false);
      setEditingService(null);
      loadServices();
    } catch (err) {
      setToast({ message: 'Erreur: ' + err?.message, type: 'error' });
    }
  };

  // Properties functions
  const loadProperties = async () => {
    try {
      setPropertiesLoading(true);
      const data = await propertyService?.getAll();
      setProperties(data);
    } catch (err) {
      setToast({ message: 'Erreur lors du chargement des propriétés', type: 'error' });
    } finally {
      setPropertiesLoading(false);
    }
  };

  const handlePropertySubmit = async (formData) => {
    try {
      if (editingProperty) {
        await propertyService?.update(editingProperty?.id, formData);
        setToast({ message: 'Propriété modifiée avec succès!', type: 'success' });
      } else {
        await propertyService?.create(formData);
        setToast({ message: 'Propriété créée avec succès!', type: 'success' });
      }
      setShowPropertyModal(false);
      setEditingProperty(null);
      loadProperties();
    } catch (err) {
      setToast({ message: 'Erreur: ' + err?.message, type: 'error' });
    }
  };

  // Vehicles functions
  const loadVehicles = async () => {
    try {
      setVehiclesLoading(true);
      const data = await vehicleService?.getAll();
      setVehicles(data);
    } catch (err) {
      setToast({ message: 'Erreur lors du chargement des véhicules', type: 'error' });
    } finally {
      setVehiclesLoading(false);
    }
  };

  const handleVehicleSubmit = async (formData) => {
    try {
      if (editingVehicle) {
        await vehicleService?.update(editingVehicle?.id, formData);
        setToast({ message: 'Véhicule modifié avec succès!', type: 'success' });
      } else {
        await vehicleService?.create(formData);
        setToast({ message: 'Véhicule créé avec succès!', type: 'success' });
      }
      setShowVehicleModal(false);
      setEditingVehicle(null);
      loadVehicles();
    } catch (err) {
      setToast({ message: 'Erreur: ' + err?.message, type: 'error' });
    }
  };

  // Delete functions
  const handleDeleteConfirm = async () => {
    try {
      if (deleteModal?.type === 'service') {
        await serviceService?.delete(deleteModal?.id);
        setToast({ message: 'Service supprimé avec succès!', type: 'success' });
        loadServices();
      } else if (deleteModal?.type === 'property') {
        await propertyService?.delete(deleteModal?.id);
        setToast({ message: 'Propriété supprimée avec succès!', type: 'success' });
        loadProperties();
      } else if (deleteModal?.type === 'vehicle') {
        await vehicleService?.delete(deleteModal?.id);
        setToast({ message: 'Véhicule supprimé avec succès!', type: 'success' });
        loadVehicles();
      }
      setDeleteModal({ show: false, type: '', id: null, name: '' });
    } catch (err) {
      setToast({ message: 'Erreur lors de la suppression: ' + err?.message, type: 'error' });
    }
  };

  // Filter data
  const filteredServices = services?.filter(service => {
    const matchesSearch = service?.title?.toLowerCase()?.includes(serviceSearchQuery?.toLowerCase());
    const matchesCategory = serviceCategory === 'all' || service?.category === serviceCategory;
    return matchesSearch && matchesCategory;
  });

  const filteredProperties = properties?.filter(property => {
    const matchesSearch = property?.title?.toLowerCase()?.includes(propertySearchQuery?.toLowerCase());
    const matchesCategory = propertyCategory === 'all' || property?.category === propertyCategory;
    return matchesSearch && matchesCategory;
  });

  const filteredVehicles = vehicles?.filter(vehicle => {
    const matchesSearch = vehicle?.name?.toLowerCase()?.includes(vehicleSearchQuery?.toLowerCase()) ||
      vehicle?.brand?.toLowerCase()?.includes(vehicleSearchQuery?.toLowerCase());
    const matchesType = vehicleType === 'all' || vehicle?.vehicleType === vehicleType;
    return matchesSearch && matchesType;
  });

  // Pagination logic
  const paginateData = (data, page) => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data?.slice(startIndex, endIndex);
  };

  const getTotalPages = (dataLength) => {
    return Math.ceil(dataLength / itemsPerPage);
  };

  const paginatedServices = paginateData(filteredServices, servicesPage);
  const paginatedProperties = paginateData(filteredProperties, propertiesPage);
  const paginatedVehicles = paginateData(filteredVehicles, vehiclesPage);

  const servicesTotalPages = getTotalPages(filteredServices?.length);
  const propertiesTotalPages = getTotalPages(filteredProperties?.length);
  const vehiclesTotalPages = getTotalPages(filteredVehicles?.length);

  // Pagination component
  const PaginationControls = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;

    return (
      <div className="flex items-center justify-between mt-6 pt-6 border-t border-border">
        <p className="text-sm text-muted-foreground">
          Page {currentPage} sur {totalPages}
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            iconName="ChevronLeft"
          >
            <span className="hidden sm:inline">Précédent</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            iconName="ChevronRight"
            iconPosition="right"
          >
            <span className="hidden sm:inline">Suivant</span>
          </Button>
        </div>
      </div>
    );
  };

  const tabs = [
    { id: 'services', label: 'Services', icon: 'Briefcase', count: services?.length },
    { id: 'properties', label: 'Immobiliers', icon: 'Home', count: properties?.length },
    { id: 'vehicles', label: 'Véhicules', icon: 'Car', count: vehicles?.length }
  ];

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    navigate(`/admin-crud-management?tab=${tabId}`, { replace: true });
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-background pt-24 md:pt-28 pb-8">
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">Gestion Administrative</h1>
            <p className="text-muted-foreground">Ajouter, modifier, supprimer et lister des services, immobiliers et véhicules</p>
          </div>

          {/* Tabs */}
          <div className="bg-card rounded-xl shadow-premium border border-border mb-6">
            <div className="flex border-b border-border overflow-x-auto">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => handleTabChange(tab?.id)}
                  className={`flex items-center gap-2 px-3 sm:px-6 py-3 sm:py-4 text-sm sm:text-base font-medium transition-colors whitespace-nowrap ${activeTab === tab?.id
                    ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'
                    }`}
                >
                  <Icon name={tab?.icon} className="w-5 h-5" />
                  {tab?.label}
                  <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-muted text-muted-foreground">
                    {tab?.count}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Services Tab */}
          {activeTab === 'services' && (
            <div className="bg-card rounded-xl shadow-premium border border-border">
              <div className="p-6 border-b border-border">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
                  <h2 className="text-2xl font-bold text-foreground">Services</h2>
                  <Button onClick={() => { setEditingService(null); setShowServiceModal(true); }} iconName="Plus" iconPosition="left">
                    Ajouter un Service
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    placeholder="Rechercher un service..."
                    value={serviceSearchQuery}
                    onChange={(e) => setServiceSearchQuery(e?.target?.value)}
                    iconName="Search"
                  />
                  <Select
                    value={serviceCategory}
                    onChange={(e) => setServiceCategory(e?.target?.value)}
                    options={[
                      { value: 'all', label: 'Toutes les catégories' },
                      { value: 'Conseil', label: 'Conseil' },
                      { value: 'Juridique', label: 'Juridique' },
                      { value: 'Immobilier', label: 'Immobilier' },
                      { value: 'Technologie', label: 'Technologie' },
                      { value: 'Événements', label: 'Événements' },
                      { value: 'Fournitures', label: 'Fournitures' },
                      { value: 'Transport', label: 'Transport' },
                      { value: 'Formation', label: 'Formation' }
                    ]}
                  />
                </div>
              </div>
              <div className="p-6">
                {servicesLoading ? (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground">Chargement des services...</p>
                  </div>
                ) : filteredServices?.length === 0 ? (
                  <div className="text-center py-12">
                    <Icon name="Package" className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                    <p className="text-muted-foreground">Aucun service trouvé</p>
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-3 px-3 md:px-4 font-semibold text-foreground">Service</th>
                            <th className="text-left py-3 px-3 md:px-4 font-semibold text-foreground hidden sm:table-cell">Catégorie</th>
                            <th className="text-left py-3 px-3 md:px-4 font-semibold text-foreground">Prix</th>
                            <th className="text-right py-3 px-3 md:px-4 font-semibold text-foreground">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedServices?.map((service) => (
                            <tr key={service?.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                              <td className="py-3 px-3 md:px-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                    <Icon name={service?.icon || 'Briefcase'} className="w-5 h-5 text-primary" />
                                  </div>
                                  <div>
                                    <p className="font-medium text-foreground">{service?.title}</p>
                                    <p className="text-sm text-muted-foreground line-clamp-1">{service?.description}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="py-3 px-3 md:px-4 hidden sm:table-cell">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                                  {service?.category}
                                </span>
                              </td>
                              <td className="py-3 px-3 md:px-4 text-foreground">{service?.startingPrice || 'Sur devis'}</td>
                              <td className="py-3 px-3 md:px-4">
                                <div className="flex items-center justify-end gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => { setEditingService(service); setShowServiceModal(true); }}
                                    iconName="Edit"
                                  />
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setDeleteModal({ show: true, type: 'service', id: service?.id, name: service?.title })}
                                    iconName="Trash2"
                                    className="text-red-500 hover:text-red-600"
                                  />
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <PaginationControls
                      currentPage={servicesPage}
                      totalPages={servicesTotalPages}
                      onPageChange={setServicesPage}
                    />
                  </>
                )}
              </div>
            </div>
          )}

          {/* Properties Tab */}
          {activeTab === 'properties' && (
            <div className="bg-card rounded-xl shadow-premium border border-border">
              <div className="p-6 border-b border-border">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
                  <h2 className="text-2xl font-bold text-foreground">Immobiliers</h2>
                  <Button onClick={() => { setEditingProperty(null); setShowPropertyModal(true); }} iconName="Plus" iconPosition="left">
                    Ajouter une Propriété
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    placeholder="Rechercher une propriété..."
                    value={propertySearchQuery}
                    onChange={(e) => setPropertySearchQuery(e?.target?.value)}
                    iconName="Search"
                  />
                  <Select
                    value={propertyCategory}
                    onChange={(e) => setPropertyCategory(e?.target?.value)}
                    options={[
                      { value: 'all', label: 'Toutes les catégories' },
                      { value: 'Vente', label: 'Vente' },
                      { value: 'Location', label: 'Location' }
                    ]}
                  />
                </div>
              </div>
              <div className="p-6">
                {propertiesLoading ? (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground">Chargement des propriétés...</p>
                  </div>
                ) : filteredProperties?.length === 0 ? (
                  <div className="text-center py-12">
                    <Icon name="Home" className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                    <p className="text-muted-foreground">Aucune propriété trouvée</p>
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-3 px-3 md:px-4 font-semibold text-foreground">Propriété</th>
                            <th className="text-left py-3 px-3 md:px-4 font-semibold text-foreground hidden sm:table-cell">Type</th>
                            <th className="text-left py-3 px-3 md:px-4 font-semibold text-foreground hidden sm:table-cell">Catégorie</th>
                            <th className="text-left py-3 px-3 md:px-4 font-semibold text-foreground">Prix</th>
                            <th className="text-right py-3 px-3 md:px-4 font-semibold text-foreground">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedProperties?.map((property) => (
                            <tr key={property?.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                              <td className="py-3 px-3 md:px-4">
                                <div className="flex items-center gap-3">
                                  {property?.image && (
                                    <img
                                      src={property?.image}
                                      alt={property?.imageAlt || property?.title}
                                      className="w-16 h-16 rounded-lg object-cover"
                                    />
                                  )}
                                  <div>
                                    <p className="font-medium text-foreground">{property?.title}</p>
                                    <p className="text-sm text-muted-foreground">{property?.location}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="py-3 px-3 md:px-4 text-foreground hidden sm:table-cell">{property?.propertyType}</td>
                              <td className="py-3 px-3 md:px-4 hidden sm:table-cell">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                                  {property?.category}
                                </span>
                              </td>
                              <td className="py-3 px-3 md:px-4 text-foreground">{property?.price?.toLocaleString()} CDF</td>
                              <td className="py-3 px-3 md:px-4">
                                <div className="flex items-center justify-end gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => { setEditingProperty(property); setShowPropertyModal(true); }}
                                    iconName="Edit"
                                  />
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setDeleteModal({ show: true, type: 'property', id: property?.id, name: property?.title })}
                                    iconName="Trash2"
                                    className="text-red-500 hover:text-red-600"
                                  />
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <PaginationControls
                      currentPage={propertiesPage}
                      totalPages={propertiesTotalPages}
                      onPageChange={setPropertiesPage}
                    />
                  </>
                )}
              </div>
            </div>
          )}

          {/* Vehicles Tab */}
          {activeTab === 'vehicles' && (
            <div className="bg-card rounded-xl shadow-premium border border-border">
              <div className="p-6 border-b border-border">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
                  <h2 className="text-2xl font-bold text-foreground">Véhicules</h2>
                  <Button onClick={() => { setEditingVehicle(null); setShowVehicleModal(true); }} iconName="Plus" iconPosition="left">
                    Ajouter un Véhicule
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    placeholder="Rechercher un véhicule..."
                    value={vehicleSearchQuery}
                    onChange={(e) => setVehicleSearchQuery(e?.target?.value)}
                    iconName="Search"
                  />
                  <Select
                    value={vehicleType}
                    onChange={(e) => setVehicleType(e?.target?.value)}
                    options={[
                      { value: 'all', label: 'Tous les types' },
                      { value: 'Sedan', label: 'Sedan' },
                      { value: 'SUV', label: 'SUV' },
                      { value: 'Van', label: 'Van' },
                      { value: 'Truck', label: 'Truck' },
                      { value: 'Luxury', label: 'Luxury' },
                      { value: 'Bus', label: 'Bus' }
                    ]}
                  />
                </div>
              </div>
              <div className="p-6">
                {vehiclesLoading ? (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground">Chargement des véhicules...</p>
                  </div>
                ) : filteredVehicles?.length === 0 ? (
                  <div className="text-center py-12">
                    <Icon name="Car" className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                    <p className="text-muted-foreground">Aucun véhicule trouvé</p>
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-3 px-3 md:px-4 font-semibold text-foreground">Véhicule</th>
                            <th className="text-left py-3 px-3 md:px-4 font-semibold text-foreground hidden sm:table-cell">Type</th>
                            <th className="text-left py-3 px-3 md:px-4 font-semibold text-foreground hidden sm:table-cell">Plaque</th>
                            <th className="text-left py-3 px-3 md:px-4 font-semibold text-foreground">Tarif/jour</th>
                            <th className="text-right py-3 px-3 md:px-4 font-semibold text-foreground">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedVehicles?.map((vehicle) => (
                            <tr key={vehicle?.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                              <td className="py-3 px-3 md:px-4">
                                <div className="flex items-center gap-3">
                                  {vehicle?.image && (
                                    <img
                                      src={vehicle?.image}
                                      alt={vehicle?.imageAlt || vehicle?.name}
                                      className="w-16 h-16 rounded-lg object-cover"
                                    />
                                  )}
                                  <div>
                                    <p className="font-medium text-foreground">{vehicle?.name}</p>
                                    <p className="text-sm text-muted-foreground">{vehicle?.brand} {vehicle?.model}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="py-3 px-3 md:px-4 text-foreground hidden sm:table-cell">{vehicle?.vehicleType}</td>
                              <td className="py-3 px-3 md:px-4 text-foreground hidden sm:table-cell">{vehicle?.licensePlate}</td>
                              <td className="py-3 px-3 md:px-4 text-foreground">{vehicle?.dailyRate?.toLocaleString()} CDF</td>
                              <td className="py-3 px-3 md:px-4">
                                <div className="flex items-center justify-end gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => { setEditingVehicle(vehicle); setShowVehicleModal(true); }}
                                    iconName="Edit"
                                  />
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setDeleteModal({ show: true, type: 'vehicle', id: vehicle?.id, name: vehicle?.name })}
                                    iconName="Trash2"
                                    className="text-red-500 hover:text-red-600"
                                  />
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <PaginationControls
                      currentPage={vehiclesPage}
                      totalPages={vehiclesTotalPages}
                      onPageChange={setVehiclesPage}
                    />
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Modals */}
        {showServiceModal && (
          <ServiceForm
            service={editingService}
            onClose={() => { setShowServiceModal(false); setEditingService(null); }}
            onSubmit={handleServiceSubmit}
          />
        )}

        {showPropertyModal && (
          <PropertyForm
            property={editingProperty}
            onClose={() => { setShowPropertyModal(false); setEditingProperty(null); }}
            onSubmit={handlePropertySubmit}
          />
        )}

        {showVehicleModal && (
          <VehicleForm
            vehicle={editingVehicle}
            onClose={() => { setShowVehicleModal(false); setEditingVehicle(null); }}
            onSubmit={handleVehicleSubmit}
          />
        )}

        {deleteModal?.show && (
          <DeleteConfirmModal
            type={deleteModal?.type}
            name={deleteModal?.name}
            onClose={() => setDeleteModal({ show: false, type: '', id: null, name: '' })}
            onConfirm={handleDeleteConfirm}
          />
        )}

        {toast && (
          <Toast
            message={toast?.message}
            type={toast?.type}
            onClose={() => setToast(null)}
          />
        )}
      </div>
      <Footer />
    </>
  );
};

export default AdminCRUDManagement;